import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking.
 * Should be called as early as possible in the application lifecycle.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    if (import.meta.env.PROD) {
      console.warn(
        '[Sentry] VITE_SENTRY_DSN is not set. Error tracking is disabled.'
      );
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay for debugging (production only)
    // Set to 0 to disable automatic session recording - only record on errors
    // This reduces bandwidth and storage costs while still capturing error sessions
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noisy errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors that are expected
      if (error instanceof Error) {
        // Ignore cancelled requests (user navigation, component unmount, etc.)
        // Using broad includes() check intentionally to catch various cancellation messages:
        // - Axios CanceledError: "canceled"
        // - AbortController: "The operation was aborted"
        // - Other libraries may include "canceled" in timeout/abort messages
        // Trade-off: May filter some legitimate timeout errors, but reduces noise significantly
        if (error.message.includes('canceled')) {
          return null;
        }
      }

      return event;
    },
  });
}

// Re-export Sentry for use throughout the app
export { Sentry };
